import { type JSX, type ReactNode, useEffect, useState } from 'react';

import { Skeleton } from '@/components/ui/skeleton.js';
import {
  fetchWorkspaceArtifactBlob,
  type WorkspaceArtifact,
} from '@/lib/api/workspace-artifacts.js';

import {
  OFFICE_PREVIEW_MAX_BYTES,
  ZIP_PREVIEW_MAX_BYTES,
} from '../artifact-display.js';
import {
  listZipEntries,
  zipTopLevel,
  type ZipTopLevelRow,
} from '../zip-listing.js';
import { ZIP_TILE_ROWS, ZipTileView } from '../ZipTreeView.js';
import {
  renderThumbnail,
  type RasterThumbnailKind,
} from './render-thumbnail.js';
import { untilAppInFront } from './thumbnail-queue.js';
import { useCachedThumbnail } from './useArtifactThumbnail.js';

/**
 * The most bytes a PDF tile downloads to draw its thumbnail. A tile reads the whole file to draw one page, so
 * a bigger PDF keeps its icon; the viewer still opens it.
 */
export const PDF_THUMBNAIL_MAX_BYTES = 25 * 1024 * 1024;

/** What every cached tile is given: the artifact, and what to show when it has no thumbnail. */
interface CachedTileProps {
  workspaceId: string;
  artifact: WorkspaceArtifact;
  fallback: ReactNode;
}

/**
 * The cache key of a tile's thumbnail. The revision is in it, so a new version draws again; so is the kind,
 * so a rename that changes what the file is never shows the old kind's drawing.
 */
function thumbnailKey(
  kind: string,
  workspaceId: string,
  artifact: WorkspaceArtifact,
): string {
  return `${kind}:${workspaceId}:${artifact.id}:${artifact.headRevision}`;
}

/**
 * The head revision's bytes, refused past `cap` — the row's size can be stale or unknown (-1). They are handed
 * on only while the app is in front, so a tab that went away during the download starts no draw.
 */
async function headBytes(
  workspaceId: string,
  artifact: WorkspaceArtifact,
  cap: number,
): Promise<ArrayBuffer> {
  const blob = await fetchWorkspaceArtifactBlob(
    workspaceId,
    artifact.id,
    artifact.headRevision,
  );
  if (blob.size > cap) throw new Error('too large for a thumbnail');
  const bytes = await blob.arrayBuffer();
  await untilAppInFront();
  return bytes;
}

/** The most bytes a tile of this kind downloads to draw its thumbnail. */
function thumbnailByteCap(kind: RasterThumbnailKind): number {
  return kind === 'pdf' ? PDF_THUMBNAIL_MAX_BYTES : OFFICE_PREVIEW_MAX_BYTES;
}

/** An object URL for the blob, revoked when the blob changes or the tile unmounts. */
function useObjectUrl(blob: Blob | null): string | null {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!blob) return;
    const created = URL.createObjectURL(blob);
    setUrl(created);
    return () => {
      URL.revokeObjectURL(created);
      setUrl(null);
    };
  }, [blob]);
  return url;
}

/**
 * A PDF or Office tile: the first page, slide or sheet, drawn once and cached in the browser under the
 * file's revision, so later visits show it without downloading the file. A file over its kind's thumbnail
 * cap keeps its icon and is never fetched.
 */
export function RasterTile({
  workspaceId,
  artifact,
  kind,
  fallback,
}: CachedTileProps & { kind: RasterThumbnailKind }): JSX.Element {
  const cap = thumbnailByteCap(kind);
  const tooLarge = artifact.size > cap;
  const state = useCachedThumbnail(
    thumbnailKey(kind, workspaceId, artifact),
    !tooLarge,
    async () =>
      renderThumbnail(kind, await headBytes(workspaceId, artifact, cap)),
  );
  const url = useObjectUrl(state.status === 'ready' ? state.blob : null);
  if (tooLarge || state.status === 'error') return <>{fallback}</>;
  if (!url) return <Skeleton className="h-full w-full rounded-none" />;
  return (
    <img
      src={url}
      alt={artifact.name}
      className="h-full w-full bg-white object-cover object-top"
    />
  );
}

/**
 * The cached top-level rows of an archive, read back from the JSON the tile stored. They are dropped when
 * the blob changes, so a tile never shows one revision's rows while it reads the next.
 */
function useZipRows(blob: Blob | null): ZipTopLevelRow[] | 'error' | null {
  const [rows, setRows] = useState<ZipTopLevelRow[] | 'error' | null>(null);
  useEffect(() => {
    if (!blob) return;
    let cancelled = false;
    blob
      .text()
      .then((text) => {
        const parsed: unknown = JSON.parse(text);
        if (!cancelled) setRows(Array.isArray(parsed) ? parsed : 'error');
      })
      .catch(() => {
        if (!cancelled) setRows('error');
      });
    return () => {
      cancelled = true;
      setRows(null);
    };
  }, [blob]);
  return rows;
}

/**
 * A zip tile: the archive's top-level folders and files. The summary — a few rows of JSON, not the archive —
 * is what is cached under the file's revision, so the list downloads an archive once, not on every visit.
 */
export function ZipTile({
  workspaceId,
  artifact,
  fallback,
}: CachedTileProps): JSX.Element {
  const tooLarge = artifact.size > ZIP_PREVIEW_MAX_BYTES;
  const state = useCachedThumbnail(
    thumbnailKey('zip', workspaceId, artifact),
    !tooLarge,
    async () => {
      const bytes = await headBytes(
        workspaceId,
        artifact,
        ZIP_PREVIEW_MAX_BYTES,
      );
      const rows = zipTopLevel(
        listZipEntries(new Uint8Array(bytes)).entries,
        ZIP_TILE_ROWS,
      );
      return new Blob([JSON.stringify(rows)], { type: 'application/json' });
    },
  );
  const rows = useZipRows(state.status === 'ready' ? state.blob : null);
  if (tooLarge || state.status === 'error' || rows === 'error') {
    return <>{fallback}</>;
  }
  if (rows === null) return <Skeleton className="h-full w-full rounded-none" />;
  if (rows.length === 0) return <>{fallback}</>;
  return <ZipTileView rows={rows} />;
}
